import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import {
  actionCloseLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { getLaborTicketModalOpen } from 'lib/selectors/getLaborTicketModalOpen';
import { getIsAuthorizationModalOpen } from 'lib/selectors/getIsAuthorizationModalOpen';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { getEmbeddedLaborTicket } from 'lib/selectors/getEmbeddedLaborTicket';
import { useRequestRecentActivitySets } from 'lib/hooks';
import { getMachine } from 'lib/selectors/getMachine';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { getLaborScopeTotalParts } from 'lib/selectors/getScopeTotalParts';
import useHandleLaborTicketSubmit from '../LaborTicket/hooks/useHandleLaborTicketSubmit';
import MMEmbeddableZoneWithLoader from '../MMEmbeddableZoneWithLoader';
import MMDrawer from '../MMDrawer';

const EmbeddedLaborTicket = ({ id, path, personId }) => {
  const dispatch = useDispatch();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const laborTicketAction = useSelector(getLaborTicketAction);
  const machine = useSelector(getMachine);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const laborScopeTotalParts = useSelector(getLaborScopeTotalParts);
  const laborTicketModalOpen = useSelector(getLaborTicketModalOpen);

  // Create close modal callback for the submit handler
  const closeModal = useCallback(() => {
    dispatch(actionCloseLaborTicketModal());
    dispatch(actionSetEmbeddedLaborTicket(null));
  }, [dispatch]);

  const handleSubmit = useHandleLaborTicketSubmit(closeModal);

  useEmbeddedEffect(id, 'labor-ticket-form:close', () => {
    dispatch(actionCloseLaborTicketModal());
    // Refetch activity sets to ensure state is in sync after embedded form closes
    refetchRecentActivitySets();
  });

  // No longer refresh on not-found; the embedded app will close itself

  useEmbeddedEffect(id, 'labor-ticket-form:submit', handleSubmit);

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path={path}
      context={{
        personId,
        action: laborTicketAction,
        enableOperatorVerification,
        opened: laborTicketModalOpen,
        laborScopeTotalPartsByMachine: machine?.machineRef
          ? { [machine.machineRef]: laborScopeTotalParts }
          : {},
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

const LaborTicketWrapper = () => {
  const dispatch = useDispatch();
  const id = 'labor-ticket-form';
  const embeddedLaborTicket = useSelector(getEmbeddedLaborTicket);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const laborTicketFormOpen = useSelector(getLaborTicketModalOpen);
  const authorizationModalOpen = useSelector(getIsAuthorizationModalOpen);

  const liveLaborTicket = embeddedLaborTicket || currentLaborTicket;

  // Pin the labor ticket when modal opens to prevent race condition
  // where subscription clears the ticket before the embedded form response arrives
  const [pinnedLaborTicket, setPinnedLaborTicket] = useState(null);

  useEffect(() => {
    if (laborTicketFormOpen && liveLaborTicket && !pinnedLaborTicket) {
      // Pin the ticket when modal opens
      setPinnedLaborTicket(liveLaborTicket);
    } else if (!laborTicketFormOpen && pinnedLaborTicket) {
      // Unpin when modal closes
      setPinnedLaborTicket(null);
    }
  }, [laborTicketFormOpen, liveLaborTicket, pinnedLaborTicket]);

  // Use pinned ticket while modal is open, fall back to live ticket
  const laborTicket = pinnedLaborTicket || liveLaborTicket;
  const laborTicketRef = laborTicket?.laborTicketRef;
  const path = laborTicketRef ? `/labor-ticket/${laborTicketRef}` : null;

  useEffect(() => {
    if (authorizationModalOpen) {
      dispatch(actionCloseLaborTicketModal());
    }
  }, [authorizationModalOpen, dispatch]);

  if (!laborTicket) {
    return null;
  }

  return (
    <MMDrawer
      isOpen={laborTicketFormOpen}
      onClose={() => {
        dispatch(actionCloseLaborTicketModal());
      }}
      title="Confirm work produced"
    >
      <EmbeddedLaborTicket
        id={id}
        path={path}
        personId={laborTicket.personId}
      />
    </MMDrawer>
  );
};

export default LaborTicketWrapper;
